import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { LiveResultsResponse, DemographicAnalyticsData } from '../types';
import { DemographicResultsSection } from '../components/DemographicResultsSection';
import { OfficialReportPdfModal } from '../components/OfficialReportPdfModal';
import { setSiteMeta } from '../utils/meta';
import {
  RefreshCw,
  CheckSquare,
  TrendingUp,
  ArrowLeft,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  ShieldCheck,
  Award,
  Users,
  PieChart,
  BarChart2,
  FileSpreadsheet,
  Download,
} from 'lucide-react';

export const ResultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const isDemographicsRequested =
    searchParams.get('tab') === 'demographics' ||
    searchParams.get('tab') === 'analytics' ||
    location.pathname.includes('/demographics') ||
    location.pathname.includes('/analytics');

  const [resultsData, setResultsData] = useState<LiveResultsResponse | null>(() => {
    try {
      const cached = localStorage.getItem('electpoll_cached_live_results');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem('electpoll_cached_live_results');
    } catch {
      return true;
    }
  });
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // Active view section ('candidates' or 'demographics')
  const [activeSection, setActiveSection] = useState<'candidates' | 'demographics'>(
    isDemographicsRequested ? 'demographics' : 'candidates'
  );
  const [demographics, setDemographics] = useState<DemographicAnalyticsData | null>(() => {
    try {
      const cached = localStorage.getItem('electpoll_cached_demographics');
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [demographicsLoading, setDemographicsLoading] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  // Password challenge state
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [savedPassword, setSavedPassword] = useState<string>(() => {
    return sessionStorage.getItem('results_access_pass') || '';
  });

  // Fetch demographic analytics
  const fetchDemographics = async (electionId?: string) => {
    try {
      setDemographicsLoading(true);
      const data = await api.getDemographicAnalytics(electionId || resultsData?.electionId || '');
      if (data && data.byLevel && data.byLevel.length > 0) {
        setDemographics(data);
      }
    } catch (err) {
      console.error('Failed to load demographic analytics', err);
    } finally {
      setDemographicsLoading(false);
    }
  };

  // Fetch results for the single election
  const fetchResults = async (passOverride?: string) => {
    const activePass = passOverride !== undefined ? passOverride : savedPassword;
    try {
      if (!resultsData) {
        setLoading(true);
      }
      setUnlockError('');
      const res = await api.getLiveResults(activePass);

      setResultsData((prev) => {
        // Monotonic protection: If we already have verified data, never regress to lower totalVoted
        if (prev && prev.totalVoted && res.totalVoted && res.totalVoted < prev.totalVoted) {
          console.warn(`Server returned fewer votes (${res.totalVoted}) than already verified (${prev.totalVoted}). Retaining higher verified count.`);
          return prev;
        }
        return res;
      });
      setLastUpdated(new Date().toLocaleTimeString());

      if (res.isProtected && !res.unlocked && passOverride) {
        setUnlockError('Invalid password. Please check your authorization code.');
      } else if (res.unlocked) {
        if (activePass) {
          sessionStorage.setItem('results_access_pass', activePass);
          setSavedPassword(activePass);
        }
        // Prefetch demographics
        fetchDemographics(res.electionId);
      }
    } catch (e) {
      console.error('Error loading results', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      searchParams.get('tab') === 'demographics' ||
      searchParams.get('tab') === 'analytics' ||
      location.pathname.includes('/demographics') ||
      location.pathname.includes('/analytics')
    ) {
      setActiveSection('demographics');
      if (!demographics && resultsData?.electionId) {
        fetchDemographics(resultsData.electionId);
      }
    } else if (searchParams.get('tab') === 'candidates') {
      setActiveSection('candidates');
    }
  }, [searchParams, location.pathname, resultsData?.electionId]);

  useEffect(() => {
    const baseTitle = resultsData?.electionTitle || 'Election';
    setSiteMeta({
      pageTitle: activeSection === 'demographics' ? `${baseTitle} Demographic Analysis` : `${baseTitle} Results`,
      description: `Live certified ballot counts and voter turnout statistics for ${baseTitle}. Powered by Electpoll.`,
    });
  }, [resultsData?.electionTitle, activeSection]);

  useEffect(() => {
    fetchResults();
    const interval = setInterval(() => {
      if (resultsData?.unlocked) {
        fetchResults();
      }
    }, 20000);
    return () => clearInterval(interval);
  }, [savedPassword]);

  // Handle password unlock submission
  const handleUnlockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput.trim()) {
      setUnlockError('Please enter the access password.');
      return;
    }
    fetchResults(passwordInput.trim());
  };

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', paddingBottom: '80px', color: '#171619' }}>
      {/* Top Navbar */}
      <header
        style={{
          backgroundColor: '#171619',
          color: '#FFFFFF',
          padding: '16px 40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #2D2C31',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/images/electpoll logo (plain white).png" alt="Electpoll Logo" style={{ height: '30px' }} />
          </Link>
          <div className="results-tally-label" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{ height: '20px', width: '1px', backgroundColor: '#2D2C31' }} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Live Tally Monitor
            </span>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => fetchResults()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: '#1E1D21',
              color: '#A1A1AA',
              border: '1px solid #2D2C31',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="results-navbar-btn-text">Sync Tally</span>
          </button>

          {resultsData && (!resultsData.isProtected || resultsData.unlocked) && (
            <button
              type="button"
              onClick={() => setPdfModalOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#FF9900',
                color: '#171619',
                border: 'none',
                padding: '8px 14px',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 153, 0, 0.25)',
              }}
            >
              <FileSpreadsheet size={14} />
              <span className="results-navbar-btn-text">Export Official PDF</span>
            </button>
          )}

          <Link
            to="/"
            style={{
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textDecoration: 'none',
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: '#2D2C31',
            }}
          >
            <ArrowLeft size={14} />
            <span className="results-navbar-btn-text">Back to Voting</span>
          </Link>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="results-main-container">
        {/* CASE 0: INITIAL LOADING STATE (PREVENTS FLASH OF UNLOCKED DASHBOARD) */}
        {loading && !resultsData ? (
          <div
            style={{
              maxWidth: '520px',
              margin: '60px auto',
              backgroundColor: '#171619',
              borderRadius: '24px',
              border: '1px solid #2D2C31',
              padding: '48px 40px',
              textAlign: 'center',
              color: '#FFFFFF',
              boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 153, 0, 0.12)',
                border: '1.5px solid #FF9900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto',
                color: '#FF9900',
              }}
            >
              <RefreshCw size={28} className="animate-spin" />
            </div>

            <span style={{ fontSize: '12px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
              Security Verification
            </span>
            <h1 style={{ fontSize: '24px', fontWeight: 800, marginTop: '8px', marginBottom: '12px' }}>
              Connecting to Live Feed
            </h1>
            <p style={{ color: '#8E8E93', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
              Checking election authorization and access credentials...
            </p>
          </div>
        ) : !resultsData ? (
          <div
            style={{
              maxWidth: '520px',
              margin: '60px auto',
              backgroundColor: '#171619',
              borderRadius: '24px',
              border: '1px solid #2D2C31',
              padding: '48px 40px',
              textAlign: 'center',
              color: '#FFFFFF',
              boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#EF4444', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
              Connection Error
            </span>
            <h1 style={{ fontSize: '22px', fontWeight: 800, marginTop: '8px', marginBottom: '12px' }}>
              Unable to Load Results
            </h1>
            <p style={{ color: '#8E8E93', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
              Could not connect to the live tally feed. Please check your network connection and try again.
            </p>
            <button
              type="button"
              onClick={() => fetchResults()}
              className="btn-primary"
              style={{
                height: '44px',
                fontSize: '14px',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '0 24px',
                margin: '0 auto',
              }}
            >
              <RefreshCw size={14} /> Retry Connection
            </button>
          </div>
        ) : resultsData.isProtected && !resultsData.unlocked ? (
          /* CASE 1: LOCKED PASSWORD CHALLENGE */
          <div
            style={{
              maxWidth: '520px',
              margin: '60px auto',
              backgroundColor: '#171619',
              borderRadius: '24px',
              border: '1px solid #2D2C31',
              padding: '48px 40px',
              textAlign: 'center',
              color: '#FFFFFF',
              boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 153, 0, 0.12)',
                border: '1.5px solid #FF9900',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto',
                color: '#FF9900',
              }}
            >
              <Lock size={28} />
            </div>

            <span style={{ fontSize: '12px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
              Access Protected
            </span>
            <h1 style={{ fontSize: '26px', fontWeight: 800, marginTop: '8px', marginBottom: '12px' }}>
              {resultsData.electionTitle || 'Election Results'}
            </h1>
            <p style={{ color: '#8E8E93', fontSize: '14px', lineHeight: 1.6, marginBottom: '32px' }}>
              This election results portal is password protected by the electoral administration. Enter your authorization access code to inspect the live tallies.
            </p>

            <form onSubmit={handleUnlockSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ position: 'relative', textAlign: 'left' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#D1D5DB', marginBottom: '8px' }}>
                  Results Access Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Enter results password"
                  style={{
                    width: '100%',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    padding: '0 48px 0 16px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '14px',
                    top: '38px',
                    background: 'none',
                    border: 'none',
                    color: '#8E8E93',
                    cursor: 'pointer',
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {unlockError && (
                <div style={{ color: '#EF4444', fontSize: '13px', textAlign: 'left', fontWeight: 500 }}>
                  ⚠️ {unlockError}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
                style={{
                  height: '48px',
                  fontSize: '15px',
                  fontWeight: 700,
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <Unlock size={16} />
                <span>{loading ? 'Verifying Authorization...' : 'Unlock Live Tallies'}</span>
              </button>
            </form>

            <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #2D2C31', fontSize: '12px', color: '#6B7280' }}>
              Protected by Electpoll Cryptographic Seal • Only authorized officials & observers can view early tallies.
            </div>
          </div>
        ) : (
          /* CASE 2: UNLOCKED OR PUBLIC RESULTS */
          <div>
            {/* Main Header Banner */}
            <div className="results-banner">
              <div className="results-banner-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div className="results-live-badge">
                    <span className="live-indicator-dot" />
                    <span>Live Election Feed</span>
                  </div>
                  {resultsData?.isProtected && (
                    <span className="results-unlocked-badge">
                      <ShieldCheck size={12} /> Password Unlocked
                    </span>
                  )}
                </div>

                <h1 className="results-banner-title">
                  {resultsData?.electionTitle || "LSS UI Election '26"} Results
                </h1>
                <div className="results-banner-subtitle">
                  Continuous feed <span className="feed-sep">•</span> Synced at {lastUpdated || 'just now'}
                </div>
              </div>

              {/* Turnout Stats Box */}
              <div className="results-stats-row">
                <div className="results-stat-box ballots-box">
                  <div className="stat-label">
                    <CheckSquare size={13} className="stat-icon-orange" />
                    <span>Ballots Cast</span>
                  </div>
                  <div className="stat-val stat-val-orange">
                    {resultsData?.totalVoted ?? (demographics && demographics.byLevel.length > 0 ? demographics.byLevel.reduce((s, l) => s + (l.voted || 0), 0) : 0)}
                  </div>
                  <div className="stat-hint">Total verified</div>
                </div>

                <div className="results-stat-box turnout-box">
                  <div className="stat-label">
                    <TrendingUp size={13} className="stat-icon-green" />
                    <span>Turnout</span>
                  </div>
                  <div className="stat-val stat-val-green">
                    {resultsData?.turnoutPercentage && resultsData.turnoutPercentage > 0
                      ? `${resultsData.turnoutPercentage}%`
                      : (demographics && demographics.byLevel.length > 0
                          ? `${(
                              (demographics.byLevel.reduce((s, l) => s + (l.voted || 0), 0) /
                                Math.max(1, demographics.byLevel.reduce((s, l) => s + (l.accredited || 0), 0))) *
                              100
                            ).toFixed(1)}%`
                          : '0.0%')}
                  </div>
                  <div className="stat-hint">Active participation</div>
                </div>
              </div>
            </div>

            {/* Section Switcher Tabs */}
            <div className="results-section-switcher">
              <div className="results-section-tabs">
                <button
                  type="button"
                  onClick={() => {
                    setActiveSection('candidates');
                    setSearchParams({});
                  }}
                  className="results-section-tab-btn"
                  style={{
                    backgroundColor: activeSection === 'candidates' ? '#171619' : 'transparent',
                    color: activeSection === 'candidates' ? '#FF9900' : '#6B7280',
                  }}
                >
                  <Award size={16} />
                  <span>Candidate Results</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveSection('demographics');
                    setSearchParams({ tab: 'demographics' });
                    if (!demographics) {
                      fetchDemographics(resultsData?.electionId);
                    }
                  }}
                  className="results-section-tab-btn"
                  style={{
                    backgroundColor: activeSection === 'demographics' ? '#171619' : 'transparent',
                    color: activeSection === 'demographics' ? '#FF9900' : '#6B7280',
                  }}
                >
                  <PieChart size={16} />
                  <span>Demographic Analytics</span>
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {activeSection === 'demographics' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6B7280' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10B981', display: 'inline-block' }} />
                    <span style={{ fontWeight: 600 }}>Segmented Turnout Intelligence</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setPdfModalOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '7px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #FF990060',
                    color: '#FF9900',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FileSpreadsheet size={14} />
                  <span>Download Official PDF</span>
                </button>
              </div>
            </div>

            {/* Conditional Views */}
            {activeSection === 'demographics' ? (
              <DemographicResultsSection
                analytics={demographics}
                loading={demographicsLoading}
                electionTitle={resultsData.electionTitle}
              />
            ) : resultsData.results && resultsData.results.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {resultsData.results.map((pos) => {
                  let totalPositionVotes = 0;
                  pos.candidates.forEach((c) => {
                    totalPositionVotes += c.votes || 0;
                  });

                  const isMulti = Boolean(pos.maxChoices && pos.maxChoices > 1);
                  const winningSeats = isMulti ? (pos.maxChoices || 1) : 1;

                  // Extract level cohort if applicable (e.g. 100 Level LSRC)
                  const levelMatch = (pos.positionName || pos.positionSlug || '').match(/(\d{3})/);
                  const levelNum = levelMatch ? levelMatch[1] : null;
                  const levelCohort = levelNum && demographics?.byLevel ? demographics.byLevel.find((l: any) => l.group.includes(levelNum)) : null;

                  // Total distinct voters who participated in this level / position
                  const positionVoters = isMulti
                    ? (levelCohort?.voted || pos.totalVoters || Math.max(...pos.candidates.map((c) => c.votes || 0), 1))
                    : (pos.totalVotes !== undefined ? pos.totalVotes : totalPositionVotes);

                  // For multi-choice positions (e.g. LSRC), percentage represents share of participating level voters who voted for that candidate
                  const percentageBase = isMulti
                    ? (positionVoters > 0 ? positionVoters : totalPositionVotes)
                    : (totalPositionVotes > 0 ? totalPositionVotes : 1);

                  // Sort candidates by votes descending
                  const sortedCandidates = [...pos.candidates].sort((a, b) => (b.votes || 0) - (a.votes || 0));

                  // Winning candidates up to winningSeats cutoff
                  const winningCandidates = sortedCandidates
                    .filter((c) => (c.votes || 0) > 0 && !c.isAbstain)
                    .slice(0, winningSeats);
                  const winningCandidateIds = new Set(winningCandidates.map((c) => c.id));

                  return (
                    <div key={pos.positionId || pos.positionSlug} className="results-position-card">
                      <div className="results-pos-header">
                        <div className="results-pos-title-group">
                          <h2 className="results-pos-title">
                            {pos.positionName || pos.positionSlug}
                          </h2>
                          <span className="results-pos-type">
                            {isMulti ? `Up to ${pos.maxChoices} choices allowed (${winningSeats} Seats)` : 'Single Choice'}
                          </span>
                        </div>
                        <div className="results-pos-badge">
                          <Users size={13} />
                          <span>{positionVoters} {isMulti ? 'Voters in Level' : 'Total votes'}</span>
                        </div>
                      </div>

                      <div className="results-candidate-list">
                        {sortedCandidates.map((cand) => {
                          const votes = cand.votes || 0;
                          const percentage = percentageBase > 0 ? ((votes / percentageBase) * 100).toFixed(1) : '0.0';
                          const isLeading = winningCandidateIds.has(cand.id);
                          const seatRank = isLeading ? winningCandidates.findIndex((c) => c.id === cand.id) + 1 : 0;

                          return (
                            <div
                              key={cand.id}
                              className={`results-candidate-item ${isLeading ? 'is-leading' : ''}`}
                            >
                              {/* Progress bar background fill */}
                              <div
                                className="results-candidate-fill"
                                style={{
                                  width: `${Math.min(parseFloat(percentage), 100)}%`,
                                  backgroundColor: isLeading ? 'rgba(255, 153, 0, 0.14)' : 'rgba(229, 231, 235, 0.4)',
                                }}
                              />

                              {/* Progress track line at bottom for enhanced visual precision */}
                              <div
                                className="results-candidate-track"
                                style={{
                                  width: `${Math.min(parseFloat(percentage), 100)}%`,
                                  backgroundColor: isLeading ? '#FF9900' : '#D1D5DB',
                                }}
                              />

                              <div className="results-candidate-content">
                                <div className="results-cand-main">
                                  <img
                                    src={cand.image || '/assets/images/userdefault.png'}
                                    alt={cand.name || 'Candidate'}
                                    className="results-cand-avatar"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = '/assets/images/indecisive.png';
                                    }}
                                  />
                                  <div className="results-cand-info">
                                    <div className="results-cand-name-row">
                                      <span className="results-cand-name">
                                        {cand.name}
                                      </span>
                                      {isLeading && (
                                        <span className="results-leading-badge">
                                          <Award size={11} /> {winningSeats > 1 ? `Leading (#${seatRank} of ${winningSeats})` : 'Leading'}
                                        </span>
                                      )}
                                    </div>
                                    {cand.nickname && (
                                      <div className="results-cand-nickname">"{cand.nickname}"</div>
                                    )}
                                    <div className="results-cand-role-tag">
                                      {cand.isAbstain ? 'Neutral Choice' : (isLeading && winningSeats > 1 ? `Council Seat #${seatRank} Winner` : 'Candidate')}
                                    </div>
                                  </div>
                                </div>

                                <div className="results-candidate-tally">
                                  <div className="results-tally-votes">
                                    {votes} <span className="results-tally-votes-label">Votes</span>
                                  </div>
                                  <div className={`results-tally-percent ${isLeading ? 'is-leading' : ''}`}>
                                    {percentage}%
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: '80px', textAlign: 'center', color: '#8E8E93', fontSize: '16px' }}>
                No ballot results registered for this election yet.
              </div>
            )}
          </div>
        )}
      </main>

      {/* Official Certified PDF Report Modal */}
      {resultsData && (
        <OfficialReportPdfModal
          isOpen={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          election={{
            id: resultsData.electionId || '',
            title: resultsData.electionTitle || "LSS UI Election '26",
            slug: resultsData.electionSlug || '',
            organizationName: resultsData.organizationName || "LSS Electoral Commission 2025/26",
            logo1Url: resultsData.logo1Url,
            logo2Url: resultsData.logo2Url,
            commissionerName: resultsData.commissionerName,
            commissionerTitle: resultsData.commissionerTitle,
            commissionerSignatureUrl: resultsData.commissionerSignatureUrl,
            observerName: resultsData.observerName,
            observerTitle: resultsData.observerTitle,
            observerSignatureUrl: resultsData.observerSignatureUrl,
            electpollRepName: resultsData.electpollRepName,
            electpollRepTitle: resultsData.electpollRepTitle,
            electpollRepSignatureUrl: resultsData.electpollRepSignatureUrl,
            status: 'active',
            turnoutPercentage: resultsData.turnoutPercentage || 0,
            totalAccredited: resultsData.totalAccredited || (demographics ? demographics.byLevel.reduce((s, l) => s + (l.accredited || 0), 0) : 0),
            totalVoted: resultsData.totalVoted || (demographics ? demographics.byLevel.reduce((s, l) => s + (l.voted || 0), 0) : 0),
          } as any}
          analytics={demographics as any}
          liveResults={resultsData}
        />
      )}
    </div>
  );
};
